import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Building2,
  Banknote,
  Calendar,
  Activity,
  Settings,
  ChevronDown,
  LogOut,
  Menu,
  X,
  UserCircle,
  Wallet,
  FolderOpen,
  BarChart3,
  Shield,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useBranding } from '@/context/BrandingContext';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const { branding, getLogoUrl } = useBranding();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard' },
    { path: '/leads', icon: FileText, label: 'Leads', testId: 'nav-leads', perm: 'view_leads' },
    { path: '/customers', icon: UserCircle, label: 'Customers', testId: 'nav-customers', perm: 'view_leads' },
    { path: '/followups', icon: Calendar, label: 'Follow-ups', testId: 'nav-followups', perm: 'view_leads' },
    { path: '/loans', icon: Wallet, label: 'Loans', testId: 'nav-loans', perm: 'view_leads' },
    { path: '/documents', icon: FolderOpen, label: 'Documents', testId: 'nav-documents', perm: 'view_leads' },
    { path: '/lenders', icon: Banknote, label: 'Lenders', testId: 'nav-lenders', perm: 'lender_management' },
    { path: '/users', icon: Users, label: 'Users', testId: 'nav-users', perm: ['user_management', 'user_creation'] },
    { path: '/roles', icon: ShieldCheck, label: 'Roles & Permissions', testId: 'nav-roles', perm: 'role_creation' },
    { path: '/branches', icon: Building2, label: 'Branches', testId: 'nav-branches', perm: 'branch_management' },
    { path: '/reports', icon: BarChart3, label: 'Reports', testId: 'nav-reports', perm: 'view_reports' },
    { path: '/activities', icon: Activity, label: 'Audit Logs', testId: 'nav-activities', perm: 'view_audit_logs' },
    { path: '/settings', icon: Settings, label: 'Settings', testId: 'nav-settings', perm: 'settings_access' },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    if (!item.perm) return true;
    return hasPermission(item.perm);
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white p-1 flex items-center justify-center">
            <img
              src={getLogoUrl()}
              alt={branding.company_name}
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-white truncate">Goldpeak</h1>
            <p className="text-xs text-white/70 truncate">{branding.tagline}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredMenuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || (item.path === '/leads' && location.pathname.startsWith('/leads'));
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              onClick={() => setIsMobileOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                isActive
                  ? 'bg-primary text-white font-medium shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Icon size={18} />
              <span className="text-sm">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-white/5">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-medium text-sm">
            {user?.full_name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
            <p className="text-xs text-white/60 capitalize truncate">{user?.role?.replace('_', ' ')}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="p-2 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors"
            title="Logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <button
        data-testid="mobile-menu-toggle"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-secondary text-white md:hidden shadow-lg"
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setIsMobileOpen(false)} />
      )}

      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-secondary flex flex-col transition-transform duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
